/**
 * GCLID Capture and Retrieval Utility
 * 
 * Handles:
 * - URL parameter capture
 * - localStorage persistence with expiry
 * - Defensive storage (doesn't overwrite valid existing values)
 * - Legacy format support (string vs JSON)
 */

/**
 * Get a URL parameter value
 * @param {string} param - Parameter name
 * @returns {string|null} Parameter value or null
 */
function getUrlParam(param) {
    // Guard: Check if window and location are available (SSR safety)
    if (typeof window === 'undefined' || !window.location) {
        return null;
    }

    const match = new RegExp('[?&]' + param + '=([^&]*)').exec(window.location.search);
    return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
}

/**
 * Get expiry record for GCLID (90 days from now)
 * @param {string} value - GCLID value
 * @returns {Object} Object with value and expiryDate
 */
function getExpiryRecord(value) {
    const expiryPeriod = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds
    const expiryDate = new Date().getTime() + expiryPeriod;
    return {
        value: value,
        expiryDate: expiryDate
    };
}

/**
 * Check if a stored GCLID is still valid (not expired)
 * @param {Object|string} gclidData - Stored GCLID data (JSON object or string)
 * @returns {boolean} True if valid, false if expired or invalid
 */
function isGclidValid(gclidData) {
    if (!gclidData) return false;

    try {
        // Handle both JSON string and object formats
        const gclidObj = typeof gclidData === 'string' ? JSON.parse(gclidData) : gclidData;

        // Check if it has required properties
        if (!gclidObj || !gclidObj.hasOwnProperty('value') || !gclidObj.hasOwnProperty('expiryDate')) {
            return false;
        }

        // Check if expired
        const now = new Date().getTime();
        return now < gclidObj.expiryDate && gclidObj.value && gclidObj.value.trim() !== '';
    } catch (e) {
        // If parsing fails, treat as invalid
        return false;
    }
}

/**
 * Validate gclsrc parameter (must be absent or contain 'aw')
 * @param {string|null} gclsrcParam - gclsrc parameter value
 * @returns {boolean} True if valid
 */
function isGclsrcValid(gclsrcParam) {
    return !gclsrcParam || gclsrcParam.indexOf('aw') !== -1;
}

/**
 * Capture GCLID from URL and persist to localStorage
 * Only overwrites existing value if:
 * - New value is valid AND
 * - Existing value is expired/missing/invalid
 * 
 * @returns {string|null} The captured GCLID value or null
 */
export function captureGclidFromUrl() {
    // Guard: Check if window and localStorage are available (SSR/Safari private mode safety)
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    try {
        // Read gclid from URL
        const gclidParam = getUrlParam('gclid');

        // Validate gclsrc if present
        const gclsrcParam = getUrlParam('gclsrc');
        if (gclidParam && !isGclsrcValid(gclsrcParam)) {
            // Invalid gclsrc, don't capture
            return null;
        }

        // Only store if we have a new gclid from URL
        if (gclidParam && gclidParam.trim() !== '') {
            // Check existing GCLID before overwriting
            const existing = localStorage.getItem('gclid');

            // Only overwrite if existing is invalid/expired/missing
            if (!isGclidValid(existing)) {
                const gclidRecord = getExpiryRecord(gclidParam);
                localStorage.setItem('gclid', JSON.stringify(gclidRecord));

                // Debug logging (can be removed in production)
                if (window.DEBUG_GCLID) {
                    console.log('[GCLID] Captured from URL:', gclidParam);
                }

                return gclidParam;
            } else {
                // Existing GCLID is valid, check if new one is different
                try {
                    const existingObj = typeof existing === 'string' ? JSON.parse(existing) : existing;
                    if (existingObj && existingObj.value !== gclidParam) {
                        // New GCLID is different from existing valid one - update it
                        // (This handles the case where user clicks a new ad)
                        const gclidRecord = getExpiryRecord(gclidParam);
                        localStorage.setItem('gclid', JSON.stringify(gclidRecord));

                        if (window.DEBUG_GCLID) {
                            console.log('[GCLID] Updated with new GCLID from URL:', gclidParam, '(replaced:', existingObj.value, ')');
                        }

                        return gclidParam;
                    } else {
                        // Same GCLID, no need to update
                        if (window.DEBUG_GCLID) {
                            console.log('[GCLID] Same GCLID in URL, keeping existing:', existingObj.value);
                        }
                        return existingObj.value;
                    }
                } catch (e) {
                    // If parsing fails, treat as invalid and store new
                    const gclidRecord = getExpiryRecord(gclidParam);
                    localStorage.setItem('gclid', JSON.stringify(gclidRecord));

                    if (window.DEBUG_GCLID) {
                        console.log('[GCLID] Captured from URL (existing was invalid):', gclidParam);
                    }

                    return gclidParam;
                }
            }
        }

        return null;
    } catch (e) {
        console.error('[GCLID] Error capturing GCLID:', e);
        return null;
    }
}

/**
 * Get GCLID from localStorage
 * Returns the value if valid and not expired, otherwise returns null
 * 
 * @returns {string|null} The GCLID value or null
 */
export function getGclid() {
    // Guard: Check if window and localStorage are available (SSR/Safari private mode safety)
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    try {
        const gclidData = localStorage.getItem('gclid');

        if (!gclidData) {
            return null;
        }

        // Check if valid
        if (!isGclidValid(gclidData)) {
            // Expired or invalid, remove it
            localStorage.removeItem('gclid');
            return null;
        }

        // Parse and return value
        const gclidObj = typeof gclidData === 'string' ? JSON.parse(gclidData) : gclidData;
        return gclidObj.value;
    } catch (e) {
        console.error('[GCLID] Error retrieving GCLID:', e);
        return null;
    }
}